import { useState, useRef } from "react";
import { toast } from "sonner";
import LucyMirror from "@/components/LucyMirror";
import { TopUpPackages } from "@/components/TopUpPackages";
import { useAuth } from "@/context/AuthContext";
import { useStreamMeter } from "@/hooks/useStreamMeter";
import { creditsToTime } from "@/components/PricingSection";
import { AlertTriangle, Coins } from "lucide-react";

const NoCreditsBanner = ({ onToppedUp }) => (
    <div className="border border-blaze/40 bg-blaze/10 p-6 mb-6" data-testid="no-credits-banner">
        <div className="flex items-center gap-3 mb-4">
            <AlertTriangle className="w-4 h-4 text-blaze" />
            <div className="font-display font-bold uppercase text-lg">
                You're out of credits
            </div>
        </div>
        <p className="font-mono text-xs text-white/60 mb-6">
            Streaming costs 2 credits per second (4 in Fast Mode). Top up below to start.
        </p>
        <TopUpPackages onToppedUp={onToppedUp} />
    </div>
);

const LiveCreditBar = ({ credits, ratePerSec, fastMode }) => (
    <div
        className="border border-white/10 bg-surface px-5 py-3 mb-6 flex items-center justify-between font-mono text-xs"
        data-testid="stream-credit-bar"
    >
        <div className="flex items-center gap-2 text-blaze uppercase tracking-[0.25em]">
            <span className="w-1.5 h-1.5 bg-blaze rounded-full live-pulse" />
            Live — metering {ratePerSec} credits/sec{fastMode ? " · Fast Mode" : ""}
        </div>
        <div className="flex items-center gap-2 text-white/70">
            <Coins className="w-3.5 h-3.5 text-blaze" />
            <span className="text-white font-bold">{credits.toLocaleString()}</span>
            <span className="text-white/40">left · ≈ {creditsToTime(credits)}</span>
        </div>
    </div>
);

export default function StreamPage() {
    const { user, updateCredits, setUser } = useAuth();
    const [connectionState, setConnectionState] = useState("idle");
    const [fastMode, setFastMode] = useState(false);
    const forceStopRef = useRef(null);

    const isLive = connectionState === "live" || connectionState === "reconnecting";
    const ratePerSec = fastMode ? 4 : 2;

    const { credits: meterCredits } = useStreamMeter({
        active: isLive,
        source: "studio",
        speed: fastMode ? "fast" : "standard",
        onCredits: updateCredits,
        onDepleted: () => {
            forceStopRef.current?.();
            toast.error("Out of credits — stream stopped. Top up to continue.");
        },
    });

    const credits = meterCredits ?? user?.credits ?? 0;

    return (
        <div data-testid="stream-page">
            {credits <= 0 && (
                <NoCreditsBanner
                    onToppedUp={(c) => setUser((prev) => (prev ? { ...prev, credits: c } : prev))}
                />
            )}
            {isLive && <LiveCreditBar credits={credits} ratePerSec={ratePerSec} fastMode={fastMode} />}
            <LucyMirror
                onConnectionStateChange={setConnectionState}
                forceStopRef={forceStopRef}
                fastMode={fastMode}
                onFastModeChange={setFastMode}
            />
        </div>
    );
}
